/*
Live data of the /mcp/ page from the radio's public feeds — the same documents the MCP
server answers from (radio.thehype.news/public-feeds/<topic>.json, CORS *). Pure
functions: check a document, count items in a window ending at the feed's own
built_at (not the browser clock), pick the freshest items. mcpPage.js fetches and renders.
*/

export const FEEDS_URL = 'https://radio.thehype.news/public-feeds';
export const FEED_TOPICS = ['news', 'funding', 'ai-morning'];
export const CHAT_STORIES = 3;
const SCHEMA_VERSION = 1;
// The radio exports every few minutes; an hour of silence means the export stalled.
const EXPORT_STALL_MS = 60 * 60 * 1000;
const LINE_CHARS = 80;
// The visitor's clock may run a little ahead of or behind the radio's.
const CLOCK_SKEW_MS = 10 * 60 * 1000;
export const DAY_MS = 24 * 3600 * 1000;
export const WEEK_MS = 7 * DAY_MS;

/**
 * A feed document fit to show: known schema, the right topic, status ok and fresh —
 * the same rules as the MCP server's warnings (thehype-mcp feeds.freshness_warnings):
 * the export ran within EXPORT_STALL_MS and its content is younger than
 * stale_after_hours.
 * @param {object} doc - parsed JSON.
 * @param {string} topic - expected topic.
 * @param {number} now - ms.
 * @returns {boolean}
 */
export function usableFeed(doc, topic, now) {
    return Boolean(doc)
        && doc.schema_version === SCHEMA_VERSION
        && doc.topic === topic
        && doc.status === 'ok'
        && !Number.isNaN(Date.parse(doc.built_at))
        && fresh(now - Date.parse(doc.generated_at), EXPORT_STALL_MS)
        && fresh(now - Date.parse(doc.content_marker_at), doc.stale_after_hours * 3600 * 1000);
}

// Age within the limit and not from the future beyond the clocks' skew.
function fresh(age, limit) {
    return age >= -CLOCK_SKEW_MS && age <= limit;
}

/**
 * Stories, newest first.
 * @param {object} doc - news feed.
 * @returns {Array<{title: string, aired: number, url: string|null}>}
 */
export function newsStories(doc) {
    return (doc.news || [])
        .filter(story => typeof story.title === 'string' && !Number.isNaN(Date.parse(story.aired_at)))
        .map(story => ({
            title: story.title,
            line: shortLine(story.summary || ''),
            aired: Date.parse(story.aired_at),
            url: safeUrl(story.source_url),
        }))
        .sort((a, b) => b.aired - a.aired);
}

/**
 * Funding rounds (not rumors), newest first by when the radio first saw them.
 * @param {object} doc - funding feed.
 * @returns {Array<{company: string, amount: string|null, lead: string|null, seen: number}>}
 */
export function fundingRounds(doc) {
    return (doc.rounds || [])
        .filter(round => typeof round.company === 'string' && !Number.isNaN(Date.parse(round.first_seen_at)))
        .map(round => ({
            company: round.company,
            amount: round.amount || null,
            lead: round.lead_investor || null,
            seen: Date.parse(round.first_seen_at),
        }))
        .sort((a, b) => b.seen - a.seen);
}

/**
 * AI Morning episodes, newest first.
 * @param {object} doc - ai-morning feed.
 * @returns {Array<{title: string, number: number, date: string}>}
 */
export function episodes(doc) {
    return (doc.episodes || [])
        .filter(item => typeof item.title === 'string' && Number.isInteger(item.episode_number))
        .map(item => ({ title: item.title, number: item.episode_number, date: String(item.date || '') }))
        .sort((a, b) => b.number - a.number);
}

/**
 * How many items fall into the window that ends when the feed was built.
 * @param {number[]} times - item times, ms.
 * @param {string} builtAt - feed built_at.
 * @param {number} windowMs - window length.
 * @returns {number}
 */
export function countWindow(times, builtAt, windowMs) {
    const end = Date.parse(builtAt);

    return times.filter(time => time <= end && time > end - windowMs).length;
}

/**
 * countWindow for a feed without a promised window: null when its oldest item is
 * inside the window — a capped export would undercount.
 * @param {number[]} times - item times, ms.
 * @param {string} builtAt - feed built_at.
 * @param {number} windowMs - window length.
 * @returns {number|null}
 */
export function coveredCount(times, builtAt, windowMs) {
    const start = Date.parse(builtAt) - windowMs;

    if (times.length === 0 || Math.min(...times) > start) {
        return null;
    }

    return countWindow(times, builtAt, windowMs);
}

/**
 * «17 min ago», «3 h ago», «2 d ago» between two moments.
 * @param {number} then - ms.
 * @param {number} now - ms.
 * @returns {string}
 */
export function ago(then, now) {
    const minutes = Math.max(0, Math.round((now - then) / 60000));

    if (minutes < 60) {
        return `${minutes} min ago`;
    }
    if (minutes < 48 * 60) {
        return `${Math.round(minutes / 60)} h ago`;
    }

    return `${Math.round(minutes / 1440)} d ago`;
}

/**
 * «16 oct», UTC.
 * @param {number} time - ms.
 * @returns {string}
 */
export function dayLabel(time) {
    const date = new Date(time);
    const month = date.toLocaleString('en', { month: 'short', timeZone: 'UTC' });

    return `${date.getUTCDate()} ${month}`;
}

/**
 * A round in one line: «RapidCanvas · $29M · led by Vertex Ventures».
 * @param {{company: string, amount: string|null, lead: string|null}} round
 * @returns {string}
 */
export function roundLine(round) {
    return [round.company, round.amount, round.lead && `led by ${round.lead}`]
        .filter(Boolean)
        .join(' · ');
}

/**
 * The first sentence of a summary, at most LINE_CHARS, cut on a word.
 * @param {string} summary
 * @returns {string}
 */
export function shortLine(summary) {
    const sentence = summary.trim().split(/(?<=[.!?])\s+/)[0];

    if (sentence.length <= LINE_CHARS) {
        return sentence;
    }
    const cut = sentence.slice(0, LINE_CHARS - 1).replace(/\s+\S*$/, '');

    return `${cut.replace(/[,;:—\-\s]+$/, '')}…`;
}

function safeUrl(value) {
    return typeof value === 'string' && value.startsWith('https://') ? value : null;
}
